import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ScrollArea } from "../components/ui/scroll-area";
import { Separator } from "../components/ui/separator";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { cn } from "../lib/utils";
import { motion, AnimatePresence } from "framer-motion";
import { useLanguage } from "../components/LanguageProvider";

const widgetTemplates = [
  { id: "kpi", label: "KPI Card", icon: "fa-chart-simple", category: "metrics", size: "small" },
  { id: "chart-line", label: "Line Chart", icon: "fa-chart-line", category: "charts", size: "medium" },
  { id: "chart-bar", label: "Bar Chart", icon: "fa-chart-bar", category: "charts", size: "medium" },
  { id: "chart-pie", label: "Pie Chart", icon: "fa-chart-pie", category: "charts", size: "medium" },
  { id: "table", label: "Data Table", icon: "fa-table", category: "data", size: "large" },
  { id: "map", label: "Map View", icon: "fa-map", category: "visualization", size: "large" },
  { id: "timeline", label: "Timeline", icon: "fa-timeline", category: "visualization", size: "medium" },
  { id: "filter", label: "Filter Panel", icon: "fa-filter", category: "controls", size: "small" },
];

const workflowSteps = [
  { id: "input", label: "Data Input", icon: "fa-database", color: "hsl(var(--chart-1))" },
  { id: "filter", label: "Filter", icon: "fa-filter", color: "hsl(var(--chart-2))" },
  { id: "transform", label: "Transform", icon: "fa-wand-magic-sparkles", color: "hsl(var(--chart-3))" },
  { id: "analyze", label: "Analyze", icon: "fa-magnifying-glass-chart", color: "hsl(var(--chart-4))" },
  { id: "output", label: "Output", icon: "fa-file-export", color: "hsl(var(--primary))" },
];

const initialWidgets = [
  { id: "w1", type: "kpi", title: "Total Entities", w: 1, h: 1, config: { value: "124.5K", change: "+12.5%" } },
  { id: "w2", type: "kpi", title: "Active Alerts", w: 1, h: 1, config: { value: "47", change: "+24" } },
  { id: "w3", type: "chart-line", title: "Activity Trend", w: 2, h: 1, config: {} },
  { id: "w4", type: "table", title: "Recent Events", w: 2, h: 1, config: {} },
];

const WidgetPreview = ({ widget, isSelected, onClick, onRemove }) => {
  const template = widgetTemplates.find(t => t.id === widget.type);
  return (
    <motion.div layout initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }} onClick={onClick}
      className={cn("relative rounded-lg border-2 bg-card transition-all cursor-pointer group min-h-[100px] lg:min-h-[120px]", isSelected ? "border-primary shadow-glow" : "border-border hover:border-primary/50", widget.w === 2 && "col-span-2")}>
      <div className="absolute top-1 right-1 lg:top-2 lg:right-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
        <Button variant="ghost" size="icon" className="h-5 w-5 lg:h-6 lg:w-6 bg-background/80" onClick={(e) => { e.stopPropagation(); onRemove(widget.id); }}><i className="fa-solid fa-xmark text-[10px] lg:text-xs"></i></Button>
      </div>
      <div className="p-2 lg:p-4 h-full flex flex-col">
        <div className="flex items-center gap-2 mb-2 lg:mb-3">
          <div className="flex h-6 w-6 lg:h-8 lg:w-8 items-center justify-center rounded-lg bg-primary/10"><i className={cn("fa-solid", template?.icon, "text-xs lg:text-sm text-primary")}></i></div>
          <h4 className="font-medium text-[10px] lg:text-sm truncate">{widget.title}</h4>
        </div>
        <div className="flex-1 flex items-center justify-center bg-muted/30 rounded-lg">
          {widget.type === "kpi" && (<div className="text-center"><p className="text-lg lg:text-2xl font-bold text-foreground">{widget.config?.value || "—"}</p><p className="text-[10px] lg:text-xs text-success">{widget.config?.change}</p></div>)}
          {widget.type === "chart-line" && (<div className="w-full h-full p-2 lg:p-4 flex items-end gap-1">{[40, 65, 45, 80, 55, 75, 90].map((h, i) => (<div key={i} className="flex-1 bg-primary/20 rounded-t" style={{ height: `${h}%` }}><div className="w-full bg-primary rounded-t" style={{ height: "20%" }}></div></div>))}</div>)}
          {widget.type === "table" && (<div className="w-full h-full p-2 space-y-1"><div className="flex gap-1">{[1,2,3].map(i => <div key={i} className="h-2 bg-muted rounded flex-1"></div>)}</div>{[1,2,3].map(i => <div key={i} className="flex gap-1 opacity-60">{[1,2,3].map(j => <div key={j} className="h-2 bg-muted rounded flex-1"></div>)}</div>)}</div>)}
          {!["kpi", "chart-line", "table"].includes(widget.type) && (<i className={cn("fa-solid", template?.icon, "text-2xl lg:text-4xl text-muted-foreground/30")}></i>)}
        </div>
      </div>
    </motion.div>
  );
};

export default function WorksheetBuilder() {
  const { t } = useLanguage();
  const [widgets, setWidgets] = useState(initialWidgets);
  const [selectedWidget, setSelectedWidget] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("widgets");
  const [showPalette, setShowPalette] = useState(false);

  const addWidget = (templateId) => {
    const template = widgetTemplates.find(t => t.id === templateId);
    const newWidget = { id: `w${Date.now()}`, type: templateId, title: template.label, w: template.size === "small" ? 1 : 2, h: 1, config: {} };
    setWidgets([...widgets, newWidget]);
    setSelectedWidget(newWidget);
    setSheetOpen(true);
    setShowPalette(false);
  };

  const removeWidget = (widgetId) => {
    setWidgets(widgets.filter(w => w.id !== widgetId));
    if (selectedWidget?.id === widgetId) { setSelectedWidget(null); setSheetOpen(false); }
  };

  const selectWidget = (widget) => { setSelectedWidget(widget); setSheetOpen(true); };

  return (
    <div className="flex flex-col lg:flex-row h-[calc(100vh-4rem)]">
      {/* Mobile Palette Toggle */}
      <div className="lg:hidden p-2 border-b border-border bg-card/50 flex items-center justify-between">
        <Button variant="outline" size="sm" onClick={() => setShowPalette(!showPalette)}><i className="fa-solid fa-shapes mr-2"></i>{t("widgetPalette")}</Button>
        <Tabs value={activeTab} onValueChange={setActiveTab}><TabsList className="h-8"><TabsTrigger value="widgets" className="text-xs h-7">{t("charts")}</TabsTrigger><TabsTrigger value="workflow" className="text-xs h-7">Workflow</TabsTrigger></TabsList></Tabs>
      </div>

      {/* Left Panel - Widget Palette */}
      <div className={cn("w-full lg:w-64 border-b lg:border-b-0 lg:border-r border-border bg-card/50 flex flex-col", showPalette ? "block" : "hidden lg:flex", "max-h-[40vh] lg:max-h-none")}>
        <div className="p-3 lg:p-4 border-b border-border hidden lg:block">
          <Tabs value={activeTab} onValueChange={setActiveTab}><TabsList className="w-full"><TabsTrigger value="widgets" className="flex-1 text-xs">{t("charts")}</TabsTrigger><TabsTrigger value="workflow" className="flex-1 text-xs">Workflow</TabsTrigger></TabsList></Tabs>
        </div>
        <ScrollArea className="flex-1 p-3 lg:p-4">
          {activeTab === "widgets" && (
            <div className="grid grid-cols-2 lg:grid-cols-1 gap-2">
              {widgetTemplates.map((template) => (
                <motion.div key={template.id} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} onClick={() => addWidget(template.id)} className="flex items-center gap-2 p-2 lg:p-3 rounded-lg border border-border bg-card hover:border-primary/50 cursor-pointer transition-colors">
                  <div className="flex h-8 w-8 lg:h-10 lg:w-10 items-center justify-center rounded-lg bg-primary/10"><i className={cn("fa-solid", template.icon, "text-xs lg:text-base text-primary")}></i></div>
                  <span className="text-[10px] lg:text-sm font-medium">{template.label}</span>
                </motion.div>
              ))}
            </div>
          )}
          {activeTab === "workflow" && (
            <div className="space-y-2">
              <p className="text-[10px] lg:text-sm text-muted-foreground">{t("dropWidgetsHere")}</p>
              {workflowSteps.map((step) => (
                <motion.div key={step.id} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} draggable className="flex items-center gap-2 lg:gap-3 p-2 lg:p-3 rounded-lg border border-border bg-card hover:border-primary/50 cursor-grab transition-colors">
                  <div className="flex h-8 w-8 lg:h-10 lg:w-10 items-center justify-center rounded-lg" style={{ backgroundColor: `${step.color}20` }}><i className={cn("fa-solid", step.icon, "text-xs lg:text-base")} style={{ color: step.color }}></i></div>
                  <span className="text-[10px] lg:text-sm font-medium">{step.label}</span>
                </motion.div>
              ))}
            </div>
          )}
        </ScrollArea>
      </div>

      {/* Main Canvas */}
      <div className="flex-1 flex flex-col overflow-hidden bg-muted/20">
        <div className="flex items-center justify-between px-3 lg:px-6 py-2 lg:py-3 border-b border-border bg-card/50 gap-2">
          <div className="flex items-center gap-2 lg:gap-4 min-w-0">
            <Input placeholder={t("worksheetCanvas")} className="w-32 lg:w-64 font-medium bg-transparent border-transparent hover:border-input focus:border-input h-8 text-xs lg:text-sm" defaultValue="Analysis Dashboard"/>
            <Badge variant="outline" className="text-[10px] lg:text-xs hidden sm:inline-flex">Draft</Badge>
          </div>
          <div className="flex items-center gap-1 lg:gap-2">
            <Button variant="outline" size="sm" className="h-7 lg:h-8 text-[10px] lg:text-xs hidden sm:flex"><i className="fa-solid fa-eye mr-1 lg:mr-2"></i>{t("view")}</Button>
            <Button size="sm" className="h-7 lg:h-8 text-[10px] lg:text-xs"><i className="fa-solid fa-save mr-1 lg:mr-2"></i><span className="hidden sm:inline">{t("save")}</span></Button>
          </div>
        </div>

        <ScrollArea className="flex-1 p-3 lg:p-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
            <AnimatePresence>
              {widgets.map((widget) => (<WidgetPreview key={widget.id} widget={widget} isSelected={selectedWidget?.id === widget.id} onClick={() => selectWidget(widget)} onRemove={removeWidget} />))}
            </AnimatePresence>
            <motion.div whileHover={{ scale: 1.02, borderColor: "hsl(var(--primary))" }} onClick={() => setShowPalette(true)} className="min-h-[100px] lg:min-h-[120px] rounded-lg border-2 border-dashed border-border flex items-center justify-center cursor-pointer hover:bg-muted/50 transition-colors">
              <div className="text-center"><i className="fa-solid fa-plus text-lg lg:text-2xl text-muted-foreground"></i><p className="text-[10px] lg:text-sm text-muted-foreground mt-1 lg:mt-2">{t("add")} Widget</p></div>
            </motion.div>
          </div>
        </ScrollArea>
      </div>

      {/* Right Panel - Widget Config */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="w-full sm:w-80 lg:w-96">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2 lg:gap-3 text-sm lg:text-base">
              {selectedWidget && (<><div className="flex h-8 w-8 lg:h-10 lg:w-10 items-center justify-center rounded-lg bg-primary/10"><i className={cn("fa-solid", widgetTemplates.find(t => t.id === selectedWidget.type)?.icon, "text-xs lg:text-base text-primary")}></i></div>{t("worksheetSettings")}</>)}
            </SheetTitle>
            <SheetDescription className="text-xs lg:text-sm">{t("description")}</SheetDescription>
          </SheetHeader>
          {selectedWidget && (
            <div className="mt-4 lg:mt-6 space-y-4">
              <div><Label htmlFor="widgetTitle" className="text-xs">{t("title")}</Label><Input id="widgetTitle" value={selectedWidget.title} onChange={(e) => { const updated = widgets.map(w => w.id === selectedWidget.id ? { ...w, title: e.target.value } : w); setWidgets(updated); setSelectedWidget({ ...selectedWidget, title: e.target.value }); }} className="h-8 lg:h-9 text-sm"/></div>
              <div><Label className="text-xs">{t("type")}</Label><Select value={selectedWidget.type} onValueChange={(val) => { const updated = widgets.map(w => w.id === selectedWidget.id ? { ...w, type: val } : w); setWidgets(updated); setSelectedWidget({ ...selectedWidget, type: val }); }}><SelectTrigger className="h-8 lg:h-9 text-sm"><SelectValue /></SelectTrigger><SelectContent>{widgetTemplates.map((template) => (<SelectItem key={template.id} value={template.id}><div className="flex items-center gap-2"><i className={cn("fa-solid", template.icon, "text-muted-foreground text-xs")}></i>{template.label}</div></SelectItem>))}</SelectContent></Select></div>
              <Separator />
              <div><Label className="text-xs">{t("source")}</Label><Select defaultValue="entities"><SelectTrigger className="h-8 lg:h-9 text-sm"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="entities">{t("entities")}</SelectItem><SelectItem value="events">{t("events")}</SelectItem><SelectItem value="metrics">{t("metrics")}</SelectItem></SelectContent></Select></div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
